import { createReducer, on, Action } from '@ngrx/store';

import * as WagerActions from './wager.actions';
import { Dictionary, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as _ from "lodash";
import { IEventDetail, IGetEventsDetailsResponse, IRace, sortEvents } from '@amwager/util-ts';
import { IEventTrackRaceState, ISelectedRaceRaceResult } from './wager.models';



export const WAGER_FEATURE_KEY = 'wager';
// export interface WagerPartialState {
//   readonly [WAGER_FEATURE_KEY]: IWagerState;
// }



/**
 * Event Track Race
 */
export const EVENT_TRACK_RACE_FEATURE_KEY = 'eventTrackRace';

export const eventTrackRaceAdapter: EntityAdapter<IGetEventsDetailsResponse> =
  createEntityAdapter<IGetEventsDetailsResponse>();

const stateGuard = (state: IEventTrackRaceState): IEventTrackRaceState => state;
const stateTypeGuard = <T>(state: T): T => state;

const initialEventTrackRaceState: IEventTrackRaceState = eventTrackRaceAdapter.getInitialState(
  stateGuard({
    id: 'event-track-race',
    name: 'event-track-race',
    selectedId: undefined,
    error: undefined,
    isLoaded: false,
    isRequestingAPI: undefined,
    ids: [] as string[] | number[],
    entities: {} as Dictionary<IGetEventsDetailsResponse>,
    selectedEventTrack: undefined,
    selectedRace: undefined,
    currentTicket: {
      payload: undefined
    },
  })
);


const eventTrackRaceReducerFN = createReducer(
  initialEventTrackRaceState,

  // clean wager
  on(WagerActions.clearWagerState, (state) => stateGuard({
    ...initialEventTrackRaceState,
    ids: state?.ids,
    entities: state?.entities,
    selectedEventTrack: state?.selectedEventTrack,
    selectedRace: state?.selectedRace
  })),

  /**
   * GET API Request
   */
  on(WagerActions.getEventsDetailsAPI, (state, { isAutoUpdate, replay }) => stateGuard({
    ...state,
    ...{
      ...initialEventTrackRaceState,
      ids: state?.ids || [],
      entities: state?.entities || {},
      selectedEventTrack: state?.selectedEventTrack || undefined,
      selectedRace: state?.selectedRace || undefined
    },
    isRequestingAPI: isAutoUpdate || replay ? false : true,
  })),

  // bet section
  on(WagerActions.getCountersAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getBetsAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.submitSavedBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.removeSavedBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.cancelBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true, // default
      isSuccess: undefined, // default
      response: undefined // default
    },
    error: undefined
  })),

  on(WagerActions.saveBetAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.betNowAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.setIsShowBetPreview, (state, { isShowBetPreview }) => stateGuard({
    ...state as IEventTrackRaceState,
    betPreview: isShowBetPreview
  })),

  on(WagerActions.getPoolMatrixAPI, (state, { requests, fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: {
      ...(state as IEventTrackRaceState).selectedRaceMatrix,
      [requests?.poolId]: {
        isProcessing: true,
        isSuccess: undefined,
        response: (() => {
          if (fromPusher) {
            return (state as IEventTrackRaceState).selectedRaceMatrix?.[requests?.poolId]?.response
          }

          return undefined;
        })()
      }
    },
    error: undefined
  })),

  on(WagerActions.getPoolsTotalsAPI, (state, { fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: true,
      isSuccess: undefined,
      response: (() => {
        if (fromPusher) {
          return (state as IEventTrackRaceState).selectedRacePoolsTotals?.response
        }

        return undefined;
      })()
    },
    error: undefined
  })),

  on(WagerActions.getWillPaysAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPI, (state, { pool, fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: true,
        isSuccess: undefined,
        response: (() => {
          if (fromPusher) {
            return (state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool]?.response
          }

          return undefined;
        })()
      },
    },
    error: undefined
  })),

  on(WagerActions.getEloRatingsAPI, (state, { requests }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceEloRatings: {
      ...(state as IEventTrackRaceState).selectedRaceEloRatings,
      [`${requests.eventId}:${requests.raceId}`]: {
        // keep cached response while refetching
        ...(state as IEventTrackRaceState).selectedRaceEloRatings?.[`${requests.eventId}:${requests.raceId}`],
        isProcessing: true,
        isSuccess: undefined,
      },
    },
  })),

  on(WagerActions.getDataProgramsAPI, (state, { date }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataPrograms: {
      ...(state as IEventTrackRaceState).dataPrograms,
      date,
      isProcessing: true,
      isSuccess: undefined,
    },
  })),

  on(WagerActions.downloadDataProgramAPI, (state, { productId }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataProgramDownload: {
      productId,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined,
      error: undefined,
    },
  })),

  // on(WagerActions.getEventRaceVideoAPI, (state) => stateGuard({
  //   ...state as IEventTrackRaceState,
  // })),

  on(WagerActions.getAllEventsNameAndIdAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getReplaysAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getRunnersAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    runners: undefined
  })),

  on(WagerActions.getEventRaceVideoReplayAPI, (state) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: undefined,
        runners: undefined,
        morningMatrixs: undefined,
        errorMsg: undefined
      },
      action: 'getEventRaceVideoReplayAPI'
    } as IRace
  })),

  on(WagerActions.getRaceResultAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        response: undefined,
        isGettingResult: true,
        isSuccessResult: undefined,
      },
      action: 'getRaceResultAPI'
    } as IRace
  })),

  on(WagerActions.getAllEventsByDateAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    getAllEventsByDate: {
      ...(state as IEventTrackRaceState).getAllEventsByDate,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),



  /**
   * GET API Response SUCCESS
   */
  on(WagerActions.getEventsDetailsAPISuccess, (state, { successResponse }) => stateGuard({
    ...eventTrackRaceAdapter.setAll(
      successResponse
        // TODO: change events to successResponse
        ?
          sortEvents(
            _.map(successResponse, (eventTrackRace: IGetEventsDetailsResponse) => {
                return {
                  id: eventTrackRace?.ToteResponse?.EventDetail?.EventId, // set an id
                  // ...eventTrackRace
                  ...{
                    ...eventTrackRace, // event from list
                    ToteResponse: {
                      ...eventTrackRace?.ToteResponse,
                      EventDetail: stateTypeGuard<IEventDetail>({
                        ...eventTrackRace?.ToteResponse?.EventDetail,
                        Races: _.map(eventTrackRace?.ToteResponse?.EventDetail?.Races, (race) => {
                          return {
                            ...race,
                            runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                            eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                          }
                        }),
                        currentRace: (() => {
                          // add currentRace for this event
                          return {
                            ..._.find(eventTrackRace?.ToteResponse?.EventDetail?.Races || [], 'Current') as IRace,
                            runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                            eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                          }
                        })()
                      })
                    },
                    eventName: eventTrackRace?.ToteResponse?.EventDetail?.EventName
                  }
                }
              }
            ),
            'MTP',
            ['Thoroughbred','Quarterhorse','Greyhound','Harness','International']
          )
        : []
      ,
      {
        ...state,
        isRequestingAPI: false,
        isLoaded: true,
      }
    )
  })),

  on(WagerActions.getEventDetailAPISuccess, (state, { eventId, successResponse, raceId }) => stateGuard({
    ...state,
    entities: (() => {
      if (_.isEmpty(successResponse)) return state?.entities;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                ...successResponse?.ToteResponse,
                EventDetail: stateTypeGuard<IEventDetail>({
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  ...successResponse?.ToteResponse?.EventDetail,
                  Races: _.map(successResponse?.ToteResponse?.EventDetail?.Races, (race) => {
                    return {
                      ...race,
                      runId: successResponse?.ToteResponse?.EventDetail?.RunId,
                      eventId: successResponse?.ToteResponse?.EventDetail?.EventId
                    }
                  }),
                  currentRace: (() => {
                    // add currentRace for this event
                    return {
                      // ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current'),
                      ...(() => {
                        return raceId
                          ? (() => { // called from updateRaceFinal (pusher)
                              const nextRace = _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]);
                              // Fall back to backend Current when no next race
                              // exists (last race final) — the old
                              // `{...undefined, Current:true} || fallback`
                              // yielded a malformed { Current:true } currentRace.
                              return nextRace
                                ? { ...nextRace, Current: true }
                                : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                            })()
                          : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current');
                      })(),
                      runId,
                      eventId
                    }
                  })()
                }),
                // RunId: runId
              },
              // eventName: state?.entities[id]?.ToteResponse?.EventDetail?.EventName
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!successResponse) return state?.selectedEventTrack;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.entities[id]?.ToteResponse,
            ...successResponse?.ToteResponse,
            EventDetail: stateTypeGuard<IEventDetail>({
              ...state?.entities[id]?.ToteResponse?.EventDetail,
              ...successResponse?.ToteResponse?.EventDetail,
              Races: _.map(successResponse?.ToteResponse?.EventDetail?.Races, (race) => {
                return {
                  ...race,
                  runId: successResponse?.ToteResponse?.EventDetail?.RunId,
                  eventId: successResponse?.ToteResponse?.EventDetail?.EventId
                }
              }),
              currentRace: (() => {
                // add currentRace for this event
                return {
                  // ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current'),
                  ...(() => {
                    return raceId
                      ? (() => { // called from updateRaceFinal (pusher)
                          const nextRace = _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]);
                          // Fall back to backend Current when no next race
                          // exists (last race final) — avoids a malformed
                          // { Current:true } currentRace.
                          return nextRace
                            ? { ...nextRace, Current: true }
                            : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                        })()
                      : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current');
                  })(),
                  runId,
                  eventId
                }
              })()
            }),
            // RunId: runId
          },
          // eventName: successResponse?.ToteResponse?.EventDetail?.EventName
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!successResponse) return state?.selectedRace;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;
      if (id === state?.selectedRace?.eventId) return {
        ...{
          ...(() => {

            const races = successResponse?.ToteResponse?.EventDetail?.Races || [];

            // `raceId` is only set by the race-final (FI) flow. Advance to
            // the next race ONLY when the race that went final is the one
            // the user is viewing; a final on any other race in the event
            // must keep them (and the matrix) on their selected race.
            if (raceId) {
              if (raceId === state?.selectedRace?.RaceId) {
                // Viewed race went final: advance to the next race, or
                // fall back to backend Current when it was the last race
                // (avoids a malformed { Current:true } selectedRace).
                const nextRace = _.find(races, ['RaceId', raceId + 1]);
                return nextRace
                  ? { ...nextRace, Current: true }
                  : (_.find(races, 'Current') || state?.selectedRace);
              }
              // A different race went final: keep the user on their race.
              return _.find(races, ['RaceId', state?.selectedRace?.RaceId])
                || state?.selectedRace;
            }

            // Event-detail refresh (results page): keep original behaviour.
            return _.find(races, 'Current');

            // return raceId
            //   ? (() => {
            //       return {
            //         ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]),
            //         Current: true
            //       } || _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
            //     })()
            //   : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
          })(),
          action: 'getEventDetailAPISuccess'
        },
        runId,
        eventId
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.getRaceResultAPISuccess, (
    state, { successResponse, eventId, previousEventsResult }
  ) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: (() => {
        if (
          // state?.selectedRace?.runId === successResponse?.ToteResponse?.RunId &&
          state?.selectedRace?.eventId === successResponse?.ToteResponse?.EventId
        ) {
          return {
            ...successResponse,
            totalHandle: (() => {
              return _.reduce(
                _.map( // select only Handle data
                  _.filter( // remove undefined or null
                    _.map( // get pool only from PoolPrices
                      successResponse?.ToteResponse?.PoolPrices,
                      (price) =>
                        _.find(
                          successResponse?.PoolsTotal,
                          (pool) => (price?.PoolId === pool?.Id) ? pool : undefined // return data from PoolPrices
                        )
                    ),
                    (filterData) => filterData
                  ),
                  (pool) => pool?.Handle
                ),
                (sum, n) => sum + n,
                0
              )
            })(),
            replayVideo: (() => {
              if (
                state?.selectedRace?.eventId === successResponse?.ToteResponse?.EventId
                && state?.selectedRace?.RaceId === successResponse?.ToteResponse?.RaceId
              ) {
                return state?.selectedRace?.raceResults?.replayVideo
              }
              else return undefined
            })(),
            response: successResponse?.ToteResponse || undefined,
            isGettingResult: false,
            isSuccessResult: true,
            previousEventsResult
          }
        }
        else return {
          response: undefined,
          isGettingResult: false,
          isSuccessResult: true,
        }
      })(),
      eventId,
      action: 'getRaceResultAPISuccess'
    } as IRace
  })),

  // bet section
  on(WagerActions.getCountersAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: false,
      isSuccess: true,
      payload: successResponse
    }
  })),

  on(WagerActions.getBetsAPISuccess, (state, { successResponse, status }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false, // default
      isSuccess: true, // default
      response: successResponse, // default
      payload: successResponse,
      [status]: successResponse
    }
  })),

  on(WagerActions.submitSavedBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.filter((bet) => bet.TicketId !== ticket.TicketId),
      response: successResponse,
      isSuccess: true,
      saved: state?.getBets?.saved?.filter((bet) => bet.TicketId !== ticket.TicketId),
    },
  })),

  on(WagerActions.removeSavedBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.filter((bet) => bet.TicketId !== ticket.TicketId),
      response: successResponse,
      isSuccess: true,
      saved: state?.getBets?.saved?.filter((bet) => bet.TicketId !== ticket.TicketId),
    },
  })),

  on(WagerActions.cancelBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.map((bet) =>
        bet.TicketId === ticket.TicketId ? ticket : bet),
      response: successResponse,
      isSuccess: true,
      active: state?.getBets?.active?.map((bet) =>
        bet.TicketId === ticket.TicketId ? ticket : bet),
    },
  })),

  on(WagerActions.saveBetAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.betNowAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  // matrix
  on(WagerActions.getPoolMatrixReplayAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    raceMatrixReplay: {
      ...(state as IEventTrackRaceState).raceMatrixReplay,
      response: successResponse
    }
  })),

  on(WagerActions.getPoolMatrixAPISuccess, (
    state, { eventId, raceId, poolId, replayMatrix, successResponse }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    // Replay mirror — pusher never writes here, so the results page keeps
    // its finished race's pools even while live updates flow below.
    replayRaceMatrix: (() => {
      if (!replayMatrix) return (state as IEventTrackRaceState).replayRaceMatrix;

      // No PoolId means an empty payload — leave the slot explicitly empty.
      if (!successResponse?.ToteResponse?.PoolId) return {
        ...(state as IEventTrackRaceState).replayRaceMatrix,
        [poolId]: undefined
      }

      return {
        ...(state as IEventTrackRaceState).replayRaceMatrix,
        [successResponse?.ToteResponse?.PoolId]: {
          isProcessing: false,
          isSuccess: true,
          response: successResponse
        }
      }
    })(),
    selectedRaceMatrix: (() => {
      if (replayMatrix) {
        return {
          ...(state as IEventTrackRaceState).selectedRaceMatrix,
          [successResponse?.ToteResponse?.PoolId]: {
            isProcessing: false,
            isSuccess: true,
            response: successResponse
          }
        }
      }
      else if (successResponse?.ToteResponse?.PoolId) {
        if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return {
            ...(state as IEventTrackRaceState).selectedRaceMatrix,
            [successResponse?.ToteResponse?.PoolId]: {
              isProcessing: false,
              isSuccess: true,
              response: successResponse
            }
          }
        }
        else return {
          ...(state as IEventTrackRaceState).selectedRaceMatrix,
          [poolId]: undefined
        }
      }
      else return {
        ...(state as IEventTrackRaceState).selectedRaceMatrix,
        [poolId]: undefined
      }
    })()
  })),

  on(WagerActions.getPoolsTotalsAPISuccess, (
    state, { eventId, raceId, replay, successResponse }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    // Replay mirror — see `replayRaceMatrix`.
    replayRacePoolsTotals: (() => {
      if (!replay) return (state as IEventTrackRaceState).replayRacePoolsTotals;

      return {
        ...(state as IEventTrackRaceState).replayRacePoolsTotals,
        isProcessing: false,
        isSuccess: true,
        response: successResponse
      }
    })(),
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: false,
      isSuccess: true,
      response: (() => {
        if (replay) {
          return successResponse
        }
        else if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return successResponse
        }
        else {
          return undefined
        }
      })()
    }
  })),

  on(WagerActions.getWillPaysAPISuccess, (
    state, { eventId, raceId, replay, successResponse }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    // Replay mirror — see `replayRaceMatrix`.
    replayRaceWillPays: (() => {
      if (!replay) return (state as IEventTrackRaceState).replayRaceWillPays;

      return {
        ...(state as IEventTrackRaceState).replayRaceWillPays,
        isProcessing: false,
        isSuccess: true,
        response: successResponse
      }
    })(),
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: false,
      isSuccess: true,
      response: (() => {
        if (replay) {
          return successResponse
        }
        else if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return successResponse
        }
        else {
          return undefined
        }
      })()
    }
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPISuccess, (
    state, { eventId, raceId, pool, replay, successResponse }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    // Replay mirror — see `replayRaceMatrix`.
    replayRaceAllOddsMatrixes: (() => {
      if (!replay) return (state as IEventTrackRaceState).replayRaceAllOddsMatrixes;

      return {
        ...(state as IEventTrackRaceState).replayRaceAllOddsMatrixes,
        [pool]: {
          ...(state as IEventTrackRaceState).replayRaceAllOddsMatrixes?.[pool],
          isProcessing: false,
          isSuccess: true,
          replayMatrix: true,
          response: successResponse
        }
      }
    })(),
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: false,
        isSuccess: true,
        replayMatrix: replay,
        response: (() => {
          if (replay) {
            return successResponse
          }
          else if (
            (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
            && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
          ) {
            return successResponse
          }
          else {
            return undefined
          }
        })()
      },
    },
  })),

  on(WagerActions.getEloRatingsAPISuccess, (state, { eventId, raceId, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceEloRatings: {
      ...(state as IEventTrackRaceState).selectedRaceEloRatings,
      [`${eventId}:${raceId}`]: {
        isProcessing: false,
        isSuccess: true,
        response: successResponse
      },
    },
  })),

  on(WagerActions.getDataProgramsAPISuccess, (state, { date, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataPrograms: {
      date,
      isProcessing: false,
      isSuccess: true,
      response: successResponse
    },
  })),

  on(WagerActions.downloadDataProgramAPISuccess, (state, { productId, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataProgramDownload: {
      productId,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
      error: undefined,
    },
  })),

  // live video
  on(WagerActions.getEventRaceVideoLiveAPISuccess, (state, { successResponse, eventId }) => stateGuard({
    ...state as IEventTrackRaceState,
    entities: (() => {
      if (_.isEmpty(successResponse)) return state?.entities;

      const id = eventId;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              streamVideo: successResponse?.Link,
              errorMsg: successResponse?.Error
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!successResponse) return state?.selectedEventTrack;

      const id = eventId;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          streamVideo: successResponse?.Link,
          errorMsg: successResponse?.Error
        }
      }

      return state?.selectedEventTrack
    })()
  })),

  // replay video
  on(WagerActions.getEventRaceVideoReplayAPISuccess, (state, { successResponse }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: successResponse?.Link,
        runners: successResponse?.Runners,
        morningMatrixs: successResponse?.MorningMatrixs,
        errorMsg: successResponse?.Error
      },
      action: 'getEventRaceVideoReplayAPISuccess'
    } as IRace
  })),

  // replay video
  on(WagerActions.setEventRaceVideoReplay, (state, { successResponse }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: undefined,
        runners: undefined,
        morningMatrixs: undefined,
        errorMsg: undefined
      },
      action: 'setEventRaceVideoReplay'
    } as IRace
  })),

  on(WagerActions.getAllEventsNameAndIdAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.getReplaysAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.getRunnersAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    runners: successResponse?.Runners
  })),

  on(WagerActions.getAllEventsByDateAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getAllEventsByDate: {
      ...(state as IEventTrackRaceState).getAllEventsByDate,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.setReplayEventsDetails, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getReplayEventsDetails: {
      ...(state as IEventTrackRaceState).getReplayEventsDetails,
      isProcessing: false,
      isSuccess: true,
      response: successResponse
      ?
        sortEvents(
          _.map(successResponse, (eventTrackRace: IGetEventsDetailsResponse) => {
              return {
                id: eventTrackRace?.ToteResponse?.EventDetail?.EventId,
                ...{
                  ...eventTrackRace, // event from list
                  ToteResponse: {
                    ...eventTrackRace?.ToteResponse,
                    EventDetail: stateTypeGuard<IEventDetail>({
                      ...eventTrackRace?.ToteResponse?.EventDetail,
                      Races: _.map(eventTrackRace?.ToteResponse?.EventDetail?.Races, (race) => {
                        return {
                          ...race,
                          runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                          eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                        }
                      }),
                      currentRace: (() => {
                        return {
                          ..._.find(
                            eventTrackRace?.ToteResponse?.EventDetail?.Races || [], 'Current'
                          ) as IRace,
                          runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                          eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                        }
                      })()
                    })
                  },
                  eventName: eventTrackRace?.ToteResponse?.EventDetail?.EventName
                }
              }
            }
          ),
          'MTP',
          ['Thoroughbred','Quarterhorse','Greyhound','Harness','International']
        )
      : [] as any
    },
  })),



  /**
   * GET API Response ERROR
   */
  on(WagerActions.getEventsDetailsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...eventTrackRaceAdapter.removeAll({
      ...state,
      ...initialEventTrackRaceState,
      error: errorResponse,
      isRequestingAPI: false
    })
  })),
  on(WagerActions.getRaceResultAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        response: errorResponse,
        isGettingResult: false,
        isSuccessResult: false,
      },
      action: 'getRaceResultAPIFailed'
    } as IRace,
    error: errorResponse,
  })),

  // bet section
  on(WagerActions.getCountersAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getBetsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.cancelBetAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false, // default
      isSuccess: false, // default
      response: errorResponse
    },
    error: errorResponse // default
  })),

  on(WagerActions.saveBetAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.betNowAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getPoolMatrixAPIFailed, (state, { errorResponse, poolId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: {
      ...(state as IEventTrackRaceState).selectedRaceMatrix,
      [poolId]: {
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
    }
  })),

  on(WagerActions.getPoolsTotalsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getWillPaysAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPIFailed, (state, { pool, errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
    },
    error: errorResponse
  })),

  on(WagerActions.getEloRatingsAPIFailed, (state, { eventId, raceId, errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceEloRatings: {
      ...(state as IEventTrackRaceState).selectedRaceEloRatings,
      [`${eventId}:${raceId}`]: {
        ...(state as IEventTrackRaceState).selectedRaceEloRatings?.[`${eventId}:${raceId}`],
        isProcessing: false,
        isSuccess: false,
      },
    },
    error: errorResponse
  })),

  // Program failures stay in their own slice: an empty or unbought card is
  // routine and must not raise the global error toast.
  on(WagerActions.getDataProgramsAPIFailed, (state, { date }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataPrograms: {
      date,
      isProcessing: false,
      isSuccess: false,
      response: []
    },
  })),

  on(WagerActions.downloadDataProgramAPIFailed, (state, { productId, errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    dataProgramDownload: {
      productId,
      isProcessing: false,
      isSuccess: false,
      response: undefined,
      error: errorResponse
    },
  })),

  on(WagerActions.getAllEventsNameAndIdAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getReplaysAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getAllEventsByDateAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getAllEventsByDate: {
      ...(state as IEventTrackRaceState).getAllEventsByDate,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.setReplayEventDetailFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedEventDetail: {
      ...(state as IEventTrackRaceState).selectedEventDetail,
      response: errorResponse
    }
  })),

  on(WagerActions.setReplayEventResultFailed, (state, { errorResponse }) => stateGuard({
    ...state,
    selectedEventResult: {
      ...(state as IEventTrackRaceState).selectedEventResult,
      response: false,
      previousEventsResult: errorResponse,
    }
  })),

  on(WagerActions.setReplayEventsDetailsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state,
    getReplayEventsDetails: {
      ...(state as IEventTrackRaceState).getReplayEventsDetails,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),



  /**
   * Properties
   */
  // on(WagerActions.setCurrentRace, (state, { id, currentRace }) => stateGuard({
  //   ...state,
  //   entities: (() => {
  //     return state?.entities[id]
  //      ?  {
  //           ...state?.entities,
  //           [id]: {
  //             ...state?.entities[id],
  //             ToteResponse: {
  //               ...state?.entities[id]?.ToteResponse,
  //               EventDetail: {
  //                 ...state?.entities[id]?.ToteResponse?.EventDetail,
  //                 currentRace
  //               }
  //             }
  //           }
  //         } as unknown as Dictionary<IGetEventsDetailsResponse>
  //      : { ...state?.entities }
  //   })()
  // })),

  on(WagerActions.selectedEventTrack, (state, { selectedEventTrack }) => stateGuard({
    ...state,
    selectedEventTrack
  })),

  on(WagerActions.selectedRace, (state, { selectedRace, eventId, runId }) => stateGuard({
    ...state,
    selectedRace: {
      ...selectedRace,
      eventId,
      runId,
      action: 'selectedRace'
    }
  })),

  // called from Pusher
  on(WagerActions.updateRace, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === newRace.RaceId)
                        ? {
                            ...newRace,
                            Current: newRace?.Current || race?.Current,
                            RaceStatus: newRace?.RaceStatus || race?.RaceStatus,
                            action: 'updateRace'
                          }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace;
                    return (currentRace?.RaceId === newRace.RaceId)
                      ? {
                          ...newRace,
                          Current: newRace?.Current || currentRace?.Current,
                          RaceStatus: newRace?.RaceStatus || currentRace?.RaceStatus,
                          action: 'updateRace'
                        }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === newRace.RaceId)
                    ? {
                        ...newRace,
                        Current: newRace?.Current || race?.Current,
                        RaceStatus: newRace?.RaceStatus || race?.RaceStatus ,
                        action: 'updateRace'
                      }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace;
                return (currentRace?.RaceId === newRace.RaceId)
                  ? {
                      ...newRace,
                      Current:  newRace?.Current || currentRace?.Current,
                      RaceStatus: newRace?.RaceStatus || currentRace?.RaceStatus,
                      action: 'updateRace'
                    }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        // OLD: spreading newRace alone dropped raceResults and other ISelectedRace-only fields
        // return (state?.selectedRace?.RaceId === newRace?.RaceId)
        //   ? { ...newRace,  Current: state?.selectedRace?.Current, RaceStatus: state?.selectedRace?.RaceStatus, action: 'updateRace' }
        //   : state?.selectedRace
        // FIX: keep existing selectedRace fields, then overlay fresh race data
        return (state?.selectedRace?.RaceId === newRace?.RaceId)
          ? { ...state?.selectedRace, ...newRace, Current: state?.selectedRace?.Current, RaceStatus: state?.selectedRace?.RaceStatus, action: 'updateRace' }
          // ? {
          //     ...newRace,
          //     Current: newRace?.Current || state?.selectedRace?.Current,
          //     RaceStatus: newRace?.RaceStatus || state?.selectedRace?.RaceStatus,
          //     action: 'updateRace'
          //   }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceFinal, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 3, action: 'updateRaceFinal' } // force status to final
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 3, action: 'updateRaceFinal' } // force status to final
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 3, action: 'updateRaceFinal' } // force status to final
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 3, action: 'updateRaceFinal' } // force status to final
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 3, action: 'updateRaceFinal' } // force status to final
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceCancelled, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? {
                            ...race,
                            RaceStatus: race.RaceStatus === 3
                              ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                              : 4, // force status to cancel
                            action: 'updateRaceCancelled'
                          }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? {
                          ...currentRace,
                          RaceStatus: currentRace.RaceStatus === 3
                            ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                            : 4,  // force status to cancel
                          action: 'updateRaceCancelled'
                        }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? {
                        ...race,
                        RaceStatus: race.RaceStatus === 3
                          ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                          : 4,  // force status to cancel
                        action: 'updateRaceCancelled'
                      }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? {
                      ...currentRace,
                      RaceStatus: currentRace.RaceStatus === 3
                        ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                        : 4,  // force status to cancel
                      action: 'updateRaceCancelled'
                    }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? {
              ...state?.selectedRace,
              RaceStatus: state?.selectedRace?.RaceStatus === 3
                ? state?.selectedRace?.RaceStatus // if RaceStatus === 3 or final race status then don't update
                : 4,  // force status to cancel
              action: 'updateRaceCancelled'
            }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceClosed, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? {
                            ...race,
                            RaceStatus: race.RaceStatus === 3
                              ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                              : 1, // force status to close
                            action: 'updateRaceClosed'
                          }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? {
                          ...currentRace,
                          RaceStatus: currentRace.RaceStatus === 3
                            ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                            : 1, // force status to close
                          action: 'updateRaceClosed'
                        } // force status to close
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? {
                        ...race,
                        RaceStatus: race.RaceStatus === 3
                          ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                          : 1, // force status to close
                        action: 'updateRaceClosed'
                      }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? {
                      ...currentRace,
                      RaceStatus: currentRace.RaceStatus === 3
                        ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                        : 1, // force status to close
                      action: 'updateRaceClosed'
                    }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? {
              ...state?.selectedRace,
              RaceStatus: state?.selectedRace?.RaceStatus === 3
                ? state?.selectedRace?.RaceStatus // if RaceStatus === 3 or final race status then don't update
                : 1, // force status to close
              action: 'updateRaceClosed'
            }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceOpen, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;


      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? {
                            ...race,
                            RaceStatus: race.RaceStatus === 3
                              ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                              : 0, // force status to open
                            action: 'updateRaceOpen'
                          }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? {
                          ...currentRace,
                          RaceStatus: currentRace.RaceStatus === 3
                            ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                            : 0, // force status to open
                          action: 'updateRaceOpen'
                        }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? {
                        ...race,
                        RaceStatus: race.RaceStatus === 3
                          ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                          : 0, // force status to open
                        action: 'updateRaceOpen'
                      }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? {
                      ...currentRace,
                      RaceStatus: currentRace.RaceStatus === 3
                        ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                        : 0, // force status to open
                      action: 'updateRaceOpen'
                    }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? {
              ...state?.selectedRace,
              RaceStatus: state?.selectedRace?.RaceStatus === 3
                ? state?.selectedRace?.RaceStatus // if RaceStatus === 3 or final race status then don't update
                : 0, // force status to open
              action: 'updateRaceOpen'
            }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRacePost, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? {
                            ...race,
                            RaceStatus: race.RaceStatus === 3
                              ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                              : 2, // force status to post
                            action: 'updateRacePost'
                          }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? {
                          ...currentRace,
                          RaceStatus: currentRace.RaceStatus === 3
                            ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                            : 2, // force status to post
                          action: 'updateRacePost'
                        }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? {
                        ...race,
                        RaceStatus: race.RaceStatus === 3
                          ? race.RaceStatus // if RaceStatus === 3 or final race status then don't update
                          : 2, // force status to post
                        action: 'updateRacePost'
                      }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? {
                      ...currentRace,
                      RaceStatus: currentRace.RaceStatus === 3
                        ? currentRace.RaceStatus // if RaceStatus === 3 or final race status then don't update
                        : 2, // force status to post
                      action: 'updateRacePost'
                    }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? {
              ...state?.selectedRace,
              RaceStatus: state?.selectedRace?.RaceStatus === 3
                ? state?.selectedRace?.RaceStatus // if RaceStatus === 3 or final race status then don't update
                : 2, // force status to post
              action: 'updateRacePost'
            }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateResultsPersonalStatistics, (state, { countersAPISuccessData }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        personalStatistic: (() => {
          const eventId: string = state?.selectedRace?.raceResults?.ToteResponse?.EventId || '';
          const raceId: string = String(state?.selectedRace?.raceResults?.ToteResponse?.RaceId) || '';

          const ticketCounter: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Count || 0;
          const ticketAmount: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Total || 0;
          const earnings: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Winnings || 0;

          return {
            ticketCounter,
            ticketAmount,
            earnings,
            profitLoss: earnings - ticketAmount
          }
        })()
      },
      action: 'updateResultsPersonalStatistics'
    } as IRace
  })),
  // countersAPISuccessData?.Counter

  // bet section
  on(WagerActions.setTicket, (state, { bet }) => stateGuard({
    ...state as IEventTrackRaceState,
    currentTicket : {
      ...(state as IEventTrackRaceState).currentTicket,
      payload: bet
    },
  })),

  on(WagerActions.setGeoLocation, (state, { location }) => stateGuard({
    ...state as IEventTrackRaceState,
    geolocation: {
      ...(state as IEventTrackRaceState).geolocation,
      ...location
    }
  })),

  on(WagerActions.updateRaceMatrix, (state, { matrix }) => stateGuard({
    ...state,
    selectedRaceMatrix: (() => {

      if (matrix) {
        return {
          ...state?.selectedRaceMatrix,
          [matrix?.PoolId]: {
            response: {
              ...state?.selectedRaceMatrix?.[matrix?.PoolId]?.response,
              ToteResponse: {
                ...state?.selectedRaceMatrix?.[matrix?.PoolId]?.response?.ToteResponse,
                ...matrix
              }
            }
          }
        }
      }

      return matrix;
    })()
  })),

  on(WagerActions.updateRacePoolsTotals, (state, { poolsTotals }) => stateGuard({
    ...state,
    selectedRacePoolsTotals: poolsTotals
  })),

  on(WagerActions.triggerMatrixModal, (state) => stateGuard({
    ...state,
    triggerMatrixModal: String((new Date()).getMilliseconds() * (Math.floor(Math.random() + 9999)))
  })),

  on(WagerActions.toUpdateGeneralEvents, (state) => stateGuard({
    ...state,
    toUpdateGeneralEvents: String((new Date()).getMilliseconds() * (Math.floor(Math.random() + 9999)))
  })),

  // live video
  on(WagerActions.setEventLiveVideo, (state, { liveEventVideo }) => stateGuard({
    ...state as IEventTrackRaceState,
    getEventLiveVideo: {
      ...(state as IEventTrackRaceState).getEventLiveVideo,
      isProcessing: false,
      isSuccess: true,
      response: liveEventVideo,
    }
  })),

  // replay info
  on(WagerActions.setSelectedReplayInfo, (
    state, { track, date, breed, race, runId, eventId, poolId, showOdds }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    replayInfo: {
      ...(state as IEventTrackRaceState).replayInfo,
      track,
      date,
      breed,
      race,
      runId,
      eventId,
      poolId,
      showOdds
    }
  })),

  // latest finished event
  on(WagerActions.setLatestFinishedEvent, (state, { latestEvent, raceId }) => stateGuard({
    ...state as IEventTrackRaceState,
    latestFinishedEvent: {
      ...(state as IEventTrackRaceState).latestFinishedEvent,
      event: latestEvent,
      raceId
    }
  })),

  // clear odds matrix
  on(WagerActions.clearSelectedRaceMatrix, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: undefined
  })),

  // clear pool totals matrix
  on(WagerActions.clearSelectedRacePoolsTotals, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: undefined
  })),

  // clear will pays matrix
  on(WagerActions.clearSelectedRaceWillPays, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: undefined
  })),

  // clear table and chart matrix
  on(WagerActions.clearRaceAllOddsMatrixes, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: undefined
  })),

  // clear the replay-only matrix mirrors (results page selection change)
  on(WagerActions.clearReplayMatrix, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    replayRaceMatrix: undefined,
    replayRacePoolsTotals: undefined,
    replayRaceWillPays: undefined,
    replayRaceAllOddsMatrixes: undefined
  })),

  // clear elo ratings cache
  on(WagerActions.clearEloRatings, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceEloRatings: undefined
  })),

  // clear the open program PDF (the list survives — it is per race date)
  on(WagerActions.clearDataProgramDownload, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    dataProgramDownload: undefined
  })),

  on(WagerActions.setDefaultEvent, (state, { isSetDefaultEvent }) => stateGuard({
    ...state as IEventTrackRaceState,
    isDefaultEvent: isSetDefaultEvent
  })),

  on(WagerActions.setEventResultToShow, (
    state, { eventId, race, runId, eventDate, runners }
  ) => stateGuard({
    ...state as IEventTrackRaceState,
    eventResultToShow: {
      ...(state as IEventTrackRaceState).eventResultToShow,
      eventId,
      race,
      runId,
      eventDate,
      runners
    }
  })),

  on(WagerActions.setEventReplayVideo, (state, { video }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: video
      },
      action: 'getEventRaceVideoReplayAPISuccess'
    } as IRace
  })),

  on(WagerActions.setPreviousEventResult, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedPreviousEventResult: {
      ...(state as IEventTrackRaceState).selectedPreviousEventResult,
      response: successResponse
    }
  })),

  on(WagerActions.setReplayEventResult, (state, { successResponse, previousEventsResult }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedEventResult: {
      ...(state as IEventTrackRaceState).selectedEventResult,
      response: successResponse,
      previousEventsResult: previousEventsResult
    }
  })),

  on(WagerActions.setReplayEventDetail, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedEventDetail: {
      ...(state as IEventTrackRaceState).selectedEventDetail,
      response: successResponse
    }
  })),

  on(WagerActions.setAllEventsByDate, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getAllEventsByDate: {
      ...(state as IEventTrackRaceState).getAllEventsByDate,
      response: successResponse
    }
  })),

  on(WagerActions.setReplayBets, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedEventReplayBet: {
      ...(state as IEventTrackRaceState).selectedEventReplayBet,
      response: successResponse
    }
  })),

  on(WagerActions.setReplayMatrix, (state, { event, race, eventId, runId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedEventReplayMatrix: {
      ...(state as IEventTrackRaceState).selectedEventReplayMatrix,
      response: {event, race, eventId, runId} as any
    }
  })),

  on(WagerActions.setRefreshWager, (state, { trigger }) => stateGuard({
    ...state as IEventTrackRaceState,
    refreshWager: trigger
  })),
);

export function eventTrackRaceReducer(state: IEventTrackRaceState | undefined, action: Action) {
  return eventTrackRaceReducerFN(state, action);
}





export interface IWagerReducer {
  'eventTrackRace': IEventTrackRaceState
}

export const wagerReducer = {
  'eventTrackRace': eventTrackRaceReducer,
}

